import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, addDoc, where } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Card, Alert } from 'react-bootstrap';

/**
 * AddPerson component for adding friends to user's account
 * Allows users to track friends' shift schedules
 */
const AddPerson = () => {
  const [nick, setNick] = useState('');
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [patterns, setPatterns] = useState({});
  const [selectedPattern, setSelectedPattern] = useState('');
  const [shifts, setShifts] = useState([]);
  const [selectedShift, setSelectedShift] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  // Fetch companies and patterns on component mount
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const shiftsRef = // Fetch from shiftPatterns collection (migrated from CSV)
collection(db, 'shiftPatterns');
        const querySnapshot = await getDocs(shiftsRef);
        
        const uniqueCompanies = [];
        const companyPatterns = {};
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          const companyName = data.company;
          const patternName = data.patternName;
          
          if (!uniqueCompanies.includes(companyName)) {
            uniqueCompanies.push(companyName);
          }
          
          if (!companyPatterns[companyName]) {
            companyPatterns[companyName] = [];
          }
          
          if (!companyPatterns[companyName].includes(patternName)) {
            companyPatterns[companyName].push(patternName);
          }
        });
        
        setCompanies(uniqueCompanies);
        setPatterns(companyPatterns);
        
        // Set default company and pattern if available
        if (uniqueCompanies.length > 0) {
          setSelectedCompany(uniqueCompanies[0]);
          if (companyPatterns[uniqueCompanies[0]] && companyPatterns[uniqueCompanies[0]].length > 0) {
            setSelectedPattern(companyPatterns[uniqueCompanies[0]][0]);
          }
        }
      } catch (error) {
        console.error("Error fetching companies:", error);
        setError("Failed to load companies. Please try again.");
      }
    };
    
    fetchCompanies();
  }, []);

  // Fetch shifts when company and pattern are selected
  useEffect(() => {
    const fetchShifts = async () => {
      if (selectedCompany && selectedPattern) {
        try {
          const shiftsRef = // Fetch from shiftPatterns collection (migrated from CSV)
collection(db, 'shiftPatterns');
          const q = query(
            shiftsRef, 
            where('company', '==', selectedCompany),
            where('patternName', '==', selectedPattern)
          );
          
          const querySnapshot = await getDocs(q);
          const shiftsData = [];
          
          querySnapshot.forEach((doc) => {
            shiftsData.push({
              id: doc.id,
              ...doc.data()
            });
          });
          
          setShifts(shiftsData);
          
          // Set default shift if available
          if (shiftsData.length > 0) {
            setSelectedShift(shiftsData[0].shift);
          }
        } catch (error) {
          console.error("Error fetching shifts:", error);
          setError("Failed to load shifts. Please try again.");
        }
      }
    };
    
    fetchShifts();
  }, [selectedCompany, selectedPattern]);

  // Handle company change
  const handleCompanyChange = (e) => {
    const company = e.target.value;
    setSelectedCompany(company);
    
    // Reset pattern and shift when company changes
    if (patterns[company] && patterns[company].length > 0) {
      setSelectedPattern(patterns[company][0]);
    } else {
      setSelectedPattern('');
    }
    setSelectedShift('');
  };

  // Handle pattern change
  const handlePatternChange = (e) => {
    setSelectedPattern(e.target.value);
    setSelectedShift('');
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!nick || !selectedCompany || !selectedPattern || !selectedShift) {
      setError('Please fill in all fields');
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User not authenticated');
      }
      
      // Find the shift ID
      const shiftsRef = // Fetch from shiftPatterns collection (migrated from CSV)
collection(db, 'shiftPatterns');
      const q = query(
        shiftsRef, 
        where('company', '==', selectedCompany),
        where('patternName', '==', selectedPattern),
        where('shift', '==', selectedShift)
      );
      
      const querySnapshot = await getDocs(q);
      let shiftId = null;
      
      querySnapshot.forEach((doc) => {
        shiftId = doc.id;
      });
      
      if (!shiftId) {
        throw new Error('Shift not found');
      }
      
      // Add friend to user's friends collection
      await addDoc(collection(db, 'users', currentUser.uid, 'friends'), {
        nick: nick,
        company: selectedCompany,
        pattern: selectedPattern,
        shift: selectedShift,
        shiftId: shiftId
      });
      
      setSuccess(true);
      setNick('');
      
      // Navigate back to home after 2 seconds
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error('Error adding person:', error);
      setError('Failed to add person. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="d-flex justify-content-center">
      <Card className="w-100" style={{ maxWidth: '500px' }}>
        <Card.Header>
          <h2 className="text-center mb-0">Add Person</h2>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">Person added successfully!</Alert>}
          
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="formNick">
              <Form.Label>Nickname</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter nickname"
                value={nick}
                onChange={(e) => setNick(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formCompany">
              <Form.Label>Company</Form.Label>
              <Form.Select 
                value={selectedCompany} 
                onChange={handleCompanyChange}
                required
              >
                <option value="">Select Company</option>
                {companies.map((company, index) => (
                  <option key={index} value={company}>{company}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formPattern">
              <Form.Label>Pattern</Form.Label>
              <Form.Select 
                value={selectedPattern} 
                onChange={handlePatternChange}
                disabled={!selectedCompany || !patterns[selectedCompany]}
                required
              >
                <option value="">Select Pattern</option>
                {selectedCompany && patterns[selectedCompany] && 
                  patterns[selectedCompany].map((pattern, index) => (
                    <option key={index} value={pattern}>{pattern}</option>
                  ))
                }
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formShift">
              <Form.Label>Shift</Form.Label>
              <Form.Select 
                value={selectedShift} 
                onChange={(e) => setSelectedShift(e.target.value)}
                disabled={!shifts.length}
                required
              >
                <option value="">Select Shift</option>
                {shifts.map((shift, index) => (
                  <option key={index} value={shift.shift}>{shift.shift}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Button 
              variant="primary" 
              type="submit" 
              className="w-100 mt-3" 
              disabled={loading}
            >
              {loading ? 'Adding...' : 'Add Person'}
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AddPerson;
